import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DRAFT_EVIDENCE, UpdateReport } from 'src/api';
import axios from 'src/axios';
import { Button } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { updateCurrentEvent } from 'src/creators/MCC.creators';
import { initialEventState } from 'src/reducers/ManualCase';
import { AppDispatch } from 'src/store';
import { CaseDocuments, UploadScanStatus } from 'src/types';
import { DraftEvent } from '../DraftEvent/DraftEvent';
import { DraftEventsEmpty } from '../DraftEventsEmpty/DraftEventsEmpty';
import { Container, EventsContainer } from './DraftEvents.styles';
import { mapEvidenceToMCCEvent } from './mapEvidenceToMCCEvent';

interface Props {
  reportID: string;
  currentReport: UpdateReport | null;
  updateReport: (id: string, report: UpdateReport) => Promise<void>;
  setIsAddingEvent: (isAddingEvent: boolean) => void;
  setIsEditingEvent: (isEditingEvent: boolean) => void;
  disabled?: boolean; // TODO: REMOVE this prop when re-enabling events
}

export const DraftEvents = ({
  reportID,
  currentReport,
  updateReport,
  setIsAddingEvent,
  setIsEditingEvent,
  disabled = false, // TODO: REMOVE this prop when re-enabling events
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const evidence = useMemo(
    () => currentReport?.evidence?.evidence ?? [],
    [currentReport],
  );
  const events = useMemo(
    () => currentReport?.evidence?.events ?? [],
    [currentReport],
  );

  const sortedEvents = useMemo(() => {
    const mapEventToMCC = (event: (typeof events)[number]) => {
      const eventEvidence = evidence.filter((e) => e.event === event.id);
      return mapEvidenceToMCCEvent(
        eventEvidence,
        String(event.id),
        event.created,
        event.updated,
      );
    };
    return events.map(mapEventToMCC);
  }, [events, evidence]);

  const [evidenceDetails, setEvidenceDetails] = useState<
    Record<string, CaseDocuments>
  >({});

  const fileCount = useMemo(() => {
    return sortedEvents.reduce((count, event) => count + event.files.length, 0);
  }, [sortedEvents]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let mounted = true;
    const MAX_POLL_TIME = 5 * 60 * 1000;
    const startTime = Date.now();

    const fetchEvidenceDetails = async () => {
      try {
        const res = await axios.get(DRAFT_EVIDENCE(reportID));
        const detailsMap: Record<string, CaseDocuments> = {};
        let hasPending = false;
        res.data.forEach((doc: CaseDocuments) => {
          detailsMap[doc.s3path] = doc;
          if (doc.scanStatus === UploadScanStatus.PENDING) {
            hasPending = true;
          }
        });
        if (mounted) {
          setEvidenceDetails(detailsMap);
        }
        return hasPending;
      } catch {
        return true;
      }
    };

    const poll = async () => {
      if (!mounted) return;
      if (Date.now() - startTime > MAX_POLL_TIME) return;

      const hasPending = await fetchEvidenceDetails();
      if (hasPending && mounted) {
        timeoutId = setTimeout(poll, 5000);
      }
    };

    if (reportID && fileCount > 0) {
      poll();
    }

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [reportID, fileCount]);

  return (
    <Container data-testid="draft-events">
      {sortedEvents.length === 0 ? (
        <DraftEventsEmpty
          setIsAddingEvent={setIsAddingEvent}
          setIsEditingEvent={setIsEditingEvent}
          disabled={disabled} // TODO: REMOVE this prop when re-enabling events
        />
      ) : (
        <>
          <EventsContainer>
            {sortedEvents.map((event) => (
              <DraftEvent
                reportID={reportID}
                event={event}
                currentReport={currentReport}
                updateReport={updateReport}
                setIsAddingEvent={setIsAddingEvent}
                setIsEditingEvent={setIsEditingEvent}
                key={event.id}
                evidenceDetails={evidenceDetails}
              />
            ))}
          </EventsContainer>
          <Button
            data-testid="add-event"
            onClick={() => {
              dispatch(updateCurrentEvent(initialEventState));
              setIsEditingEvent(false);
              setIsAddingEvent(true);
            }}
            variant="primary"
            size="small"
            width="120px"
            style={{ alignSelf: 'center' }}
            disabled={disabled} // TODO: REMOVE this disabled prop when re-enabling events
          >
            <StyledIconInButton
              size="small"
              position="left"
              className="uil uil-plus"
            />
            Add event
          </Button>
        </>
      )}
    </Container>
  );
};
